import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/firebaseConfig";
import { CoreError, CoreErrorCause } from "../../domain/models/CoreError";

/**
 * NetworkingService provides Firebase callable function invocation utilities.
 */
export class NetworkingService {
  private static instance: NetworkingService;

  private constructor() {
    // Intentionally empty.
  }

  public static getInstance(): NetworkingService {
    if (!NetworkingService.instance) {
      NetworkingService.instance = new NetworkingService();
    }
    return NetworkingService.instance;
  }

  public async callCallable<TResponse, TRequest = Record<string, unknown>>(
    callableName: string,
    parameters?: TRequest,
  ): Promise<TResponse> {
    try {
      const callable = httpsCallable(functions, callableName);
      const response = await callable(parameters ?? {});
      return response.data as TResponse;
    } catch (error) {
      if (error instanceof CoreError) {
        throw error;
      }
      throw this.mapCallableError(error, callableName);
    }
  }

  private mapCallableError(error: unknown, callableName: string): CoreError {
    const err = (error ?? {}) as { code?: string; message?: string };
    const code = String(err.code || "").toLowerCase();
    const message = String(
      err.message || `Callable function ${callableName} failed`,
    );

    if (code.includes("unauthenticated")) {
      return new CoreError({
        cause: CoreErrorCause.InvalidCredential,
        context: message,
        underlyingException: error,
      });
    }

    if (code.includes("permission-denied")) {
      return new CoreError({
        cause: CoreErrorCause.PermissionDenied,
        context: message,
        underlyingException: error,
      });
    }

    if (
      code.includes("unavailable") ||
      code.includes("deadline-exceeded") ||
      code.includes("cancelled") ||
      code.includes("network")
    ) {
      return new CoreError({
        cause: CoreErrorCause.NetworkError,
        context: message,
        underlyingException: error,
      });
    }

    // Client-caused rejections the backend signals with expected HttpsError
    // codes (e.g. an unknown invitation code) — surfaced in UI, not Sentry.
    if (code.includes("not-found")) {
      return new CoreError({
        cause: CoreErrorCause.DataNotFound,
        context: message,
        underlyingException: error,
        reportToSentry: false,
      });
    }

    if (code.includes("already-exists")) {
      return new CoreError({
        cause: CoreErrorCause.InvitationCodeAlreadyUsed,
        context: message,
        underlyingException: error,
        reportToSentry: false,
      });
    }

    if (
      code.includes("invalid-argument") ||
      code.includes("failed-precondition") ||
      code.includes("out-of-range")
    ) {
      return new CoreError({
        cause: CoreErrorCause.InvalidInvitationCode,
        context: message,
        underlyingException: error,
        reportToSentry: false,
      });
    }

    // Anything else (e.g. `internal`) is a genuine backend fault — keep it
    // visible in Sentry, and don't mislabel it as a network problem.
    return new CoreError({
      cause: CoreErrorCause.Unknown,
      context: message,
      underlyingException: error,
    });
  }
}
